import { ChipRow } from "../../modals/PreferencesModal";
import {
  ALLERGENS_QUESTION,
  FLAVORS_AVOID_QUESTION,
  FLAVORS_PREFER_QUESTION
} from "../../../lib/questionCopy";
import type { AboutYouFuel } from "./useAboutYouFuel";

/**
 * "Needs & tastes" (wizard step "needs", D-16) — allergens + the flavor prefer/avoid
 * rows, split from the old combined About-you step. The flavor rows are ALWAYS
 * visible (the round-3 collapse pattern was removed). Never blocks Continue.
 *
 * Renders the inner `.fgp-wz-fuel` block only — the caller owns the chrome.
 *
 * If the option lists failed to load, the headings still render (the athlete
 * must know allergens exist) with one shared retry line in place of the chips.
 * A load that succeeded with an empty list still hides that section.
 */
export function NeedsTastesStep({ fuel }: { fuel: AboutYouFuel }) {
  const failed = fuel.optionsLoadFailed;
  return (
    <div className="fgp-wz-fuel">
      {failed && (
        <p className="fgp-wz-fuel__desc" role="status">
          Couldn&apos;t load the options.{" "}
          <button type="button" className="fgp-wz-ramplink" onClick={fuel.retryOptions}>
            Retry
          </button>
        </p>
      )}
      {/* ── Allergens — safety inputs are never a disclosure (guardrail 6) ── */}
      {(failed || fuel.hasDietarySection) && (
        <div className="fgp-wz-field">
          <div id="fgp-wz-allergens-label" className="fgp-wz-fuel__label">
            {ALLERGENS_QUESTION.label}{" "}
            <span className="fgp-wz-fuel__label-hint">— optional</span>
          </div>
          <p className="fgp-wz-fuel__desc">{ALLERGENS_QUESTION.desc}</p>
          {!failed && (
            <>
              <ChipRow
                ariaLabelledby="fgp-wz-allergens-label"
                options={fuel.dietaryOptions}
                selected={fuel.dietaryNeeds}
                onToggle={(key) => {
                  fuel.dietaryTouchedRef.current = true;
                  fuel.setDietaryNeeds((s) => {
                    const next = new Set(s);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }}
                tone="purple"
              />
              {/* W-06 — neutral empty state, not a warning. */}
              {fuel.dietaryNeeds.size === 0 ? (
                <p className="fgp-wz-fuel__desc">{ALLERGENS_QUESTION.emptyNote}</p>
              ) : null}
            </>
          )}
        </div>
      )}
      {(failed || fuel.flavorOptions.length > 0) && (
        <>
          <div className="fgp-wz-field">
            <div id="fgp-wz-flavors-prefer-label" className="fgp-wz-fuel__label">
              {FLAVORS_PREFER_QUESTION.label}{" "}
              <span className="fgp-wz-fuel__label-hint">— optional</span>
            </div>
            <p className="fgp-wz-fuel__desc">{FLAVORS_PREFER_QUESTION.desc}</p>
            {!failed && (
              <ChipRow
                ariaLabelledby="fgp-wz-flavors-prefer-label"
                options={fuel.flavorOptions}
                selected={fuel.flavorPrefs}
                onToggle={(key) => {
                  fuel.flavorTouchedRef.current = true;
                  fuel.setFlavorPrefs((s) => {
                    const next = new Set(s);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                  // Preferring a flavor drops it from the avoid row.
                  fuel.setFlavorAvoids((s) => {
                    if (!s.has(key)) return s;
                    const next = new Set(s);
                    next.delete(key);
                    return next;
                  });
                }}
                tone="lime"
              />
            )}
          </div>
          <div className="fgp-wz-field">
            <div id="fgp-wz-flavors-avoid-label" className="fgp-wz-fuel__label">
              {FLAVORS_AVOID_QUESTION.label}{" "}
              <span className="fgp-wz-fuel__label-hint">— optional</span>
            </div>
            <p className="fgp-wz-fuel__desc">{FLAVORS_AVOID_QUESTION.desc}</p>
            {!failed && (
              <ChipRow
                ariaLabelledby="fgp-wz-flavors-avoid-label"
                options={fuel.flavorOptions}
                selected={fuel.flavorAvoids}
                onToggle={(key) => {
                  fuel.flavorTouchedRef.current = true;
                  fuel.setFlavorAvoids((s) => {
                    const next = new Set(s);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                  // Avoiding a flavor drops it from the prefer row.
                  fuel.setFlavorPrefs((s) => {
                    if (!s.has(key)) return s;
                    const next = new Set(s);
                    next.delete(key);
                    return next;
                  });
                }}
                tone="purple"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
